class MobilePredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.mobile-header-search__input');
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.mobileHost = this.closest('mobile-header-search');
    this.categoryInput = this.mobileHost?.querySelector('.js-mobile-search-category');
    this.hiddenForm = this.mobileHost?.querySelector('.predictive_search_mobile_form');
    this.predictiveSearchResults = this.mobileHost?.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.mobile-predictive-search-status');

    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.debouncedOnChange = this.debounce(this.onChange.bind(this), 300);
    this.onKeyup = this.onKeyup.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.setupEventListeners();
  }

  debounce(fn, wait) {
    if (typeof debounce === 'function') return debounce(fn, wait);
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  setupEventListeners() {
    if (!this.input) return;
    this.input.form?.addEventListener('submit', this.onFormSubmit);
    this.input.addEventListener('input', this.debouncedOnChange);
    this.mobileHost?.addEventListener('keyup', this.onKeyup);
    this.mobileHost?.addEventListener('keydown', this.onKeydown);
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clearResults();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.mobileHost?.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onKeyup(event) {
    if (!this.mobileHost?.classList.contains('is-active')) return;
    if (!this.getQuery().length) return;

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
      default:
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.mobileHost?.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.mobileHost?.querySelectorAll('.mobile-header-search__results li, .mobile-header-search__results .mobile-predictive-search__view-all') || []
    ).filter((element) => element.offsetParent !== null);

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    for (let i = 0; i < allVisibleElements.length; i += 1) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
        break;
      }
    }

    if (this.statusElement) this.statusElement.textContent = '';

    let activeElementIndex = 0;
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.mobileHost?.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedOption) selectedOption.click();
  }

  getPredictiveSearchUrl() {
    if (typeof routes !== 'undefined' && routes.predictive_search_url) {
      return routes.predictive_search_url;
    }
    return `${window.shopUrl || window.location.origin}/search/suggest`;
  }

  parseResultsMarkup(text) {
    const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
    const selectors = [
      '#shopify-section-predictive-search-mobile',
      '[id*="predictive-search-mobile"]',
      '#predictive-search-results-mobile',
    ];

    for (let i = 0; i < selectors.length; i += 1) {
      const section = parsedDocument.querySelector(selectors[i]);
      if (section?.innerHTML?.trim()) return section.innerHTML;
    }

    return '';
  }

  getSearchResults(searchTerm) {
    if (!this.hiddenForm) return;

    const formData = new FormData(this.hiddenForm);
    const params = new URLSearchParams(formData);

    if (this.categoryInput?.value.length > 0) {
      searchTerm = `${this.categoryInput.value} ${searchTerm}`;
    }

    params.set('q', searchTerm);
    const urlParams = params.toString();
    const queryKey = urlParams.toLowerCase();

    this.setLiveRegionLoadingState();
    this.mobileHost?.classList.add('has-results');

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${this.getPredictiveSearchUrl()}?${urlParams}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = this.parseResultsMarkup(text);
        if (!resultsMarkup) {
          this.showSearchError('No results found.');
          return;
        }
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.showSearchError('Search is temporarily unavailable.');
      })
      .finally(() => {
        this.removeAttribute('loading');
      });
  }

  showSearchError(message) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = `<div class="mobile-predictive-search__no-results">${message}</div>`;
    this.mobileHost?.classList.add('has-results');
    this.setAttribute('results', true);
    this.open();
  }

  setLiveRegionLoadingState() {
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.mobileHost?.classList.add('has-results');
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.predictiveSearchResults?.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) this.setLiveRegionText(liveRegion.textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input?.setAttribute('aria-expanded', true);
  }

  clearResults() {
    const selected = this.mobileHost?.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input?.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input?.setAttribute('aria-expanded', false);

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = '';
    }

    if (!this.getQuery().length) {
      this.mobileHost?.classList.remove('has-results');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.searchTerm = '';
    }
    this.clearResults();
  }
}

class MobileHeaderSearch extends HTMLElement {
  static SNIZE_SELECTORS = [
    '#snize-instant-search-results',
    '.snize-ac-results-content',
    '.snize-mobile-top-panel',
    '.snize-ac-results',
    '.snize-ac-results-mobile',
    '.snize-new-design',
    '#snize_instant_search',
    '#snize-modal',
    '.snize-modal',
  ];

  static OFFSET_SELECTORS = [
    '[data-bstndsls-bar-uuid]',
    '[class*="bstndsls-sh-bar"]',
    'sticky-header',
  ];

  static getHeaderOffsetTop() {
    let bottom = 0;

    MobileHeaderSearch.OFFSET_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.height > 0) {
          bottom = Math.max(bottom, rect.bottom);
        }
      });
    });

    return Math.max(0, Math.round(bottom));
  }

  constructor() {
    super();
    this.shell = this.querySelector('.mobile-header-search__shell');
    this.panel = this.querySelector('.mobile-header-search__panel');
    this.predictiveSearch = this.querySelector('mobile-predictive-search');
    this.form = this.querySelector('.mobile-header-search__form');
    this.trigger = this.querySelector('.mobile-header-search__trigger');
    this.inputArea = this.querySelector('.mobile-header-search__input-area');
    this.input = this.querySelector('.mobile-header-search__input');
    this.categoryInput = this.querySelector('.js-mobile-search-category');
    this.categoryButtons = this.querySelectorAll('.mobile-header-search__category');

    this.onEscape = this.onEscapeKey.bind(this);
    this.onCategoryClick = this.onCategoryClick.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerTouchStart = this.onTriggerTouchStart.bind(this);
    this.onSubmitClick = this.onSubmitClick.bind(this);
    this.triggerOpenedFromTouch = false;
    this.onInputFocus = this.onInputFocus.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
  }

  connectedCallback() {
    this.prepareInput();
    this.deactivateInput();

    this.trigger?.addEventListener('touchstart', this.onTriggerTouchStart, { passive: false });
    this.trigger?.addEventListener('click', this.onTriggerClick);
    this.form?.addEventListener('submit', this.onSubmitClick);
    this.input?.addEventListener('focus', this.onInputFocus, true);

    this.categoryButtons.forEach((button) => {
      button.addEventListener('click', this.onCategoryClick);
    });
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('touchstart', this.onTriggerTouchStart);
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.form?.removeEventListener('submit', this.onSubmitClick);
    this.input?.removeEventListener('focus', this.onInputFocus, true);
    this.categoryButtons.forEach((button) => {
      button.removeEventListener('click', this.onCategoryClick);
    });
    document.removeEventListener('keydown', this.onEscape);
    document.removeEventListener('click', this.onDocumentClick);
    this.removeViewportListeners();
    this.closeModal(false);
  }

  updateShellOffset() {
    const top = MobileHeaderSearch.getHeaderOffsetTop();
    this.style.setProperty('--mobile-header-search-top', `${top}px`);
  }

  addViewportListeners() {
    this.removeViewportListeners();
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
  }

  removeViewportListeners() {
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange);
  }

  onViewportChange() {
    if (!this.classList.contains('is-active')) return;
    this.updateShellOffset();
  }

  prepareInput() {
    if (!this.input) return;
    this.input.classList.remove('snize-input-style', 'snize-ac-input');
  }

  hideSnizeUI() {
    MobileHeaderSearch.SNIZE_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        element.style.setProperty('display', 'none', 'important');
        element.style.setProperty('visibility', 'hidden', 'important');
        element.classList.add('snize-hidden');
      });
    });

    if (typeof window.Searchanise !== 'undefined') {
      try {
        window.Searchanise.closeResults?.();
        window.Searchanise.closeInstantSearch?.();
      } catch (error) {
        /* ignore */
      }
    }
  }

  activateInput() {
    if (!this.input) return;
    this.prepareInput();
    this.input.setAttribute('name', 'q');
    this.input.removeAttribute('readonly');
    this.input.tabIndex = 0;
  }

  focusSearchInput() {
    if (!this.input) return;

    try {
      this.input.focus({ preventScroll: true });
      const length = this.input.value.length;
      this.input.setSelectionRange(length, length);
    } catch (error) {
      this.input.focus();
    }
  }

  deactivateInput() {
    if (!this.input) return;
    this.input.removeAttribute('name');
    this.input.setAttribute('readonly', 'readonly');
    this.input.tabIndex = -1;
    this.input.blur();
  }

  onTriggerTouchStart(event) {
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    this.triggerOpenedFromTouch = true;
    this.openModal(true);
    window.setTimeout(() => {
      this.triggerOpenedFromTouch = false;
    }, 500);
  }

  onTriggerClick(event) {
    if (this.triggerOpenedFromTouch) return;
    event.preventDefault();
    event.stopPropagation();
    this.openModal(true);
  }

  onSubmitClick(event) {
    if (this.classList.contains('is-active')) return;
    event.preventDefault();
    this.openModal(true);
  }

  onInputFocus() {
    if (!this.classList.contains('is-active')) {
      this.openModal(true);
      return;
    }

    this.hideSnizeUI();
  }

  openModal(focusInput = false) {
    if (this.classList.contains('is-active')) {
      if (focusInput) this.focusSearchInput();
      return;
    }

    this.hideSnizeUI();
    this.updateShellOffset();
    this.addViewportListeners();
    document.body.classList.add('mobile-header-search-open');
    this.classList.add('is-active');
    this.activateInput();
    this.panel?.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.onEscape);
    document.addEventListener('click', this.onDocumentClick);

    if (focusInput) {
      this.focusSearchInput();
    }

    requestAnimationFrame(() => {
      this.updateShellOffset();
    });
  }

  closeModal(clearInput = false) {
    this.classList.remove('is-active', 'has-results');
    this.panel?.setAttribute('aria-hidden', 'true');
    this.style.removeProperty('--mobile-header-search-top');
    this.removeViewportListeners();
    document.body.classList.remove('mobile-header-search-open');
    document.removeEventListener('keydown', this.onEscape);
    document.removeEventListener('click', this.onDocumentClick);

    this.deactivateInput();
    this.hideSnizeUI();

    this.categoryButtons.forEach((item) => item.classList.remove('is-active'));
    if (this.categoryInput) this.categoryInput.value = '';

    if (clearInput && this.predictiveSearch?.closeResults) {
      this.predictiveSearch.closeResults(true);
    } else if (this.predictiveSearch?.clearResults) {
      this.predictiveSearch.clearResults();
    }
  }

  onDocumentClick(event) {
    if (!this.classList.contains('is-active')) return;
    if (this.contains(event.target)) return;
    this.closeModal(false);
  }

  onEscapeKey(event) {
    if (event.code === 'Escape') {
      event.preventDefault();
      this.closeModal(true);
    }
  }

  onCategoryClick(event) {
    const button = event.currentTarget;
    const isActive = button.classList.contains('is-active');
    const wasOpen = this.classList.contains('is-active');

    if (!isActive && !this.input?.value.trim() && button.dataset.categoryUrl) {
      window.location.href = button.dataset.categoryUrl;
      return;
    }

    if (!wasOpen) {
      this.openModal(false);
    }

    this.categoryButtons.forEach((item) => item.classList.remove('is-active'));

    if (isActive) {
      if (this.categoryInput) this.categoryInput.value = '';
    } else {
      button.classList.add('is-active');
      if (this.categoryInput) {
        this.categoryInput.value = button.dataset.categoryFilter || button.dataset.categoryLabel || '';
      }
    }

    if (this.input?.value.trim() && typeof this.predictiveSearch?.onChange === 'function') {
      this.predictiveSearch.onChange();
    }
  }
}

if (!customElements.get('mobile-predictive-search')) {
  customElements.define('mobile-predictive-search', MobilePredictiveSearch);
}

if (!customElements.get('mobile-header-search')) {
  customElements.define('mobile-header-search', MobileHeaderSearch);
}
